import { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const TEXTE_CONSENTEMENT = "J'accepte que ma position GPS soit partagée avec mon employeur lorsque l'application est ouverte (article L1121-1 du Code du travail).";

const estChauffeur = (u) => !!u && (u.role === 'chauffeur' || /chauffeur/i.test(u.poste || ''));

// Suivi GPS automatique des chauffeurs dès que l'application est ouverte
export const SuiviGPSAuto = () => {
  const { user } = useAuth();
  const [etat, setEtat] = useState('chargement');
  const [demanderConsentement, setDemanderConsentement] = useState(false);
  const timerRef = useRef(null);
  const reglageRef = useRef({ suivi_maintenant: true, frequence_min: 1 });
  const actif = estChauffeur(user);

  const envoyerPosition = useCallback(() => {
    if (!('geolocation' in navigator) || document.visibilityState !== 'visible') return;
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        const r = await api.post('/geoloc/ping', {
          lat: p.coords.latitude,
          lon: p.coords.longitude,
          accuracy: p.coords.accuracy || null,
          heading: p.coords.heading || null,
          speed: p.coords.speed != null ? p.coords.speed * 3.6 : null,
          is_background: false,
        });
        setEtat(r.data?.ignore ? 'pause' : 'actif');
      } catch (e) {
        if (e?.response?.status === 403) setDemanderConsentement(true);
        setEtat('erreur');
      }
    }, (err) => setEtat(err?.code === 1 ? 'refuse' : 'erreur'),
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 30000 });
  }, []);

  const lireReglage = useCallback(async () => {
    try {
      reglageRef.current = (await api.get('/reglages-suivi/mon-suivi')).data;
    } catch (_) {}
    const r = reglageRef.current;
    if (r.exclu) setEtat('exclu');
    else if (!r.suivi_maintenant) setEtat('pause');
    return r;
  }, []);

  // Une minute par tour : relit les réglages toutes les 10 min, envoie la position selon la fréquence choisie
  const demarrer = useCallback(async () => {
    clearInterval(timerRef.current);
    let tour = 0;
    const r = await lireReglage();
    if (!r.exclu && r.suivi_maintenant) envoyerPosition();
    timerRef.current = setInterval(async () => {
      tour += 1;
      const reg = tour % 10 === 0 ? await lireReglage() : reglageRef.current;
      if (!reg.exclu && reg.suivi_maintenant && tour % Math.max(1, reg.frequence_min) === 0) envoyerPosition();
    }, 60 * 1000);
  }, [envoyerPosition, lireReglage]);

  useEffect(() => {
    if (!actif) return undefined;
    let annule = false;
    api.get('/geoloc/consent').then((r) => {
      if (annule) return;
      if (r.data?.has_consented) demarrer();
      else setDemanderConsentement(true);
    }).catch(() => {});
    const auRetour = () => {
      const r = reglageRef.current;
      if (document.visibilityState === 'visible' && !r.exclu && r.suivi_maintenant) envoyerPosition();
    };
    document.addEventListener('visibilitychange', auRetour);
    return () => {
      annule = true;
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [actif, demarrer, envoyerPosition]);

  const accepter = async () => {
    await api.post('/geoloc/consent', { consent: true, text_consent: TEXTE_CONSENTEMENT, user_agent: navigator.userAgent });
    setDemanderConsentement(false);
    demarrer();
  };

  if (!actif || etat === 'exclu' || etat === 'chargement') return null;
  return (
    <>
      <div
        data-testid="suivi-gps-auto-badge"
        data-etat={etat}
        className={`fixed top-2 right-2 z-50 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium shadow-md ${etat === 'actif' ? 'bg-emerald-600 text-white' : etat === 'pause' ? 'bg-slate-500 text-white' : 'bg-amber-500 text-white'}`}
      >
        <MapPin className="h-3 w-3" />
        {{ actif: 'GPS actif', refuse: 'GPS refusé', pause: 'Suivi en pause' }[etat] || 'GPS en attente'}
      </div>
      <Dialog open={demanderConsentement} onOpenChange={setDemanderConsentement}>
        <DialogContent data-testid="suivi-gps-consentement-dialog">
          <DialogHeader>
            <DialogTitle>Partage de votre position</DialogTitle>
            <DialogDescription>{TEXTE_CONSENTEMENT}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="suivi-gps-refuser-btn" onClick={() => setDemanderConsentement(false)}>Plus tard</Button>
            <Button data-testid="suivi-gps-accepter-btn" onClick={accepter}>J'accepte</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SuiviGPSAuto;
